import { Instance, SnapshotIn, SnapshotOut, types } from 'mobx-state-tree';
import { IUserImageAreaModel } from './userImageArea';
import { CanvasBorderType } from '@photosi/expo-photosi-sdk/build/ImageEditor';
import { ProductCategoryCode } from '@photosi/expo-photosi-sdk/build/Product';
import { PreviewModel, ViewBoxModel } from './preview';

export type TPreviewCanvasModel = typeof PreviewCanvasModel;
export type PreviewCanvasModelInstance = Instance<TPreviewCanvasModel>;
export interface IPreviewCanvasModelIn extends SnapshotIn<TPreviewCanvasModel> {}
export interface IPreviewCanvasModelOut extends SnapshotOut<TPreviewCanvasModel> {}
export interface IPreviewCanvasModel extends PreviewCanvasModelInstance {}

export const PreviewCanvasModel = PreviewModel.named('PreviewCanvasModel')
  .props({
    type: types.literal(ProductCategoryCode.CANVAS),
    viewBox: ViewBoxModel,
    borderWidth: types.optional(types.number, 0),
  })
  .views(self => ({
    get innerUserImageArea(): IUserImageAreaModel {
      return <IUserImageAreaModel>(
        self.userImageAreas.find(userImageArea => userImageArea.id === 'userImageArea01WithoutBorders')
      );
    },
    get outerUserImageArea(): IUserImageAreaModel {
      return <IUserImageAreaModel>(
        self.userImageAreas.find(userImageArea => userImageArea.id === 'userImageArea01WithBorders')
      );
    },
  }))
  .views(self => ({
    get isMultiphoto(): boolean {
      return false;
    },
    get userImageArea(): IUserImageAreaModel {
      const currentUserImage = self.innerUserImageArea.userImage ?? self.outerUserImageArea.userImage;
      if (currentUserImage?.borderType !== CanvasBorderType.FOLDED) {
        return self.innerUserImageArea;
      } else {
        return self.outerUserImageArea;
      }
    },
  }))
  .views(self => ({
    get blankCornersPosition() {
      const relativeUserImageArea: IUserImageAreaModel = self.outerUserImageArea;
      return {
        topLeft: {
          top: relativeUserImageArea.y,
          left: relativeUserImageArea.x,
          width: self.borderWidth,
          height: self.borderWidth,
        },
        topRight: {
          top: relativeUserImageArea.y,
          left: relativeUserImageArea.x + relativeUserImageArea.width - self.borderWidth,
          width: self.borderWidth,
          height: self.borderWidth,
        },
        bottomRight: {
          top: relativeUserImageArea.y + relativeUserImageArea.height - self.borderWidth,
          left: relativeUserImageArea.x + relativeUserImageArea.width - self.borderWidth,
          width: self.borderWidth,
          height: self.borderWidth,
        },
        bottomLeft: {
          top: relativeUserImageArea.y + relativeUserImageArea.height - self.borderWidth,
          left: relativeUserImageArea.x,
          width: self.borderWidth,
          height: self.borderWidth,
        },
      };
    },
    get bordersPositions() {
      const relativeUserImageArea: IUserImageAreaModel = self.outerUserImageArea;
      return {
        top: {
          top: relativeUserImageArea.y,
          left: relativeUserImageArea.x + self.borderWidth,
          width: relativeUserImageArea.width - 2 * self.borderWidth,
          height: self.borderWidth,
        },
        right: {
          top: relativeUserImageArea.y + self.borderWidth,
          left: relativeUserImageArea.x + relativeUserImageArea.width - self.borderWidth,
          width: self.borderWidth,
          height: relativeUserImageArea.height - 2 * self.borderWidth,
        },
        bottom: {
          top: relativeUserImageArea.y + relativeUserImageArea.height - self.borderWidth,
          left: relativeUserImageArea.x + self.borderWidth,
          width: relativeUserImageArea.width - 2 * self.borderWidth,
          height: self.borderWidth,
        },
        left: {
          top: relativeUserImageArea.y + self.borderWidth,
          left: relativeUserImageArea.x,
          width: self.borderWidth,
          height: relativeUserImageArea.height - 2 * self.borderWidth,
        },
      };
    },
  }));
